import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import './communicatie.css';

/**
 * Een knop met een klein menu eronder (`.a-menu`, zoals het avatarmenu van de schil).
 *
 * Voor de filter "Soort" op de agenda en het menu "Meer" bij een document. Niet in de kit: het
 * avatarmenu daar hangt vast onder de kopbalk, dit menu hangt aan zijn knop. Escape en een klik
 * ernaast sluiten; pijltjes lopen door de items; de focus gaat bij sluiten terug naar de knop.
 */

export interface MenuEntry {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  /** Voor een keuzemenu (filter): het gekozen item krijgt `aria-checked`. */
  checked?: boolean;
  testId?: string;
  onSelect: () => void;
}

export function MenuButton({
  trigger,
  items,
  label,
  radio,
}: {
  /** De knop zelf; krijgt `aria-haspopup`, `aria-expanded` en de klik via de props. */
  trigger: (props: {
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string;
    onClick: () => void;
  }) => ReactNode;
  items: MenuEntry[];
  /** Het `aria-label` van het menu. */
  label: string;
  /** Een keuzemenu: items zijn `menuitemradio`. */
  radio?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const trigger = wrap.current?.querySelector<HTMLElement>('[aria-haspopup]');
    menu.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus();

    const onPointer = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Niet ook een paneel eromheen sluiten: Escape sluit eerst het menu.
        event.stopPropagation();
        setOpen(false);
        trigger?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const entries = Array.from(
      menu.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [],
    );
    const index = entries.indexOf(document.activeElement as HTMLElement);
    const next = (index + (event.key === 'ArrowDown' ? 1 : -1) + entries.length) % entries.length;
    entries[next]?.focus();
  };

  return (
    <div className="c-menu-wrap" ref={wrap}>
      {trigger({
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': id,
        onClick: () => setOpen((current) => !current),
      })}
      {open ? (
        <div
          ref={menu}
          id={id}
          role="menu"
          aria-label={label}
          className="a-menu c-menu"
          onKeyDown={onKeyDown}
        >
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role={radio ? 'menuitemradio' : 'menuitem'}
              aria-checked={radio ? Boolean(item.checked) : undefined}
              className="a-menu-item"
              data-testid={item.testId}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
